import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PromotionBlockConfig } from '@front-platform/content-builder';
import { promotionBlockResolveConfig } from '@front-platform/content-builder';
import { Component, computed, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';

import { ProductKeys } from '@config/keys/product.keys';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel, IProductLabel } from '@core/models/product.model';
import {
  EligiblePromotion,
  getEligiblePromotionProductId,
} from '@core/services/api/bogof/bogof-api.service';
import { ModalsService } from '@core/services/modals/modals.service';
import type { ProductAddModalConfig } from '@shared/modals/product-add-modal/product-add-modal.component';
import type { KioskProductOverviewModalConfig } from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';

import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { selectEligiblePromotionsData } from '@store/eligible-promotions/eligible-promotions.selector';
import { SELECT_SETTINGS_PARAMETERS } from '@store/settings/settings.selection';
import { modeMatchesPromotionAllowedModes } from '@shared/utils/promotion-distribution-mode/promotion-distribution-mode.util';
export interface PromotionBlockRuntimeConfig {
  text_color?: IContentBuilderFieldColor;
  card_color?: IContentBuilderFieldColor;
  button_background_color?: IContentBuilderFieldColor;
  button_text_color?: IContentBuilderFieldColor;
  price_color?: IContentBuilderFieldColor;
  secondary_color?: IContentBuilderFieldColor;
}

export type PromotionBlockConfigInput = Partial<
  Omit<PromotionBlockConfig, keyof PromotionBlockRuntimeConfig> & PromotionBlockRuntimeConfig
>;
export type PromotionBlockResolvedConfig = Omit<
  PromotionBlockConfig,
  keyof PromotionBlockRuntimeConfig
> &
  Partial<PromotionBlockRuntimeConfig>;
export interface PromotionBlockContract extends ConfigComponentContract<
  PromotionBlockConfigInput,
  PromotionBlockResolvedConfig
> {}
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { ProductService } from '@core/services/product/product.service';

@Component({
  selector: 'app-promotion-block',
  templateUrl: './promotion-block.component.html',
  standalone: false,
})
export class PromotionBlockComponent implements PromotionBlockContract {
  readonly config = input<PromotionBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PromotionBlockConfig,
    PromotionBlockRuntimeConfig
  >(promotionBlockResolveConfig, () => ({ config: this.config() }));
  /** Optional title override (default: "Nos Promos") */
  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as TranslationField);
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly priceColor = computed(
    () => this.resolvedConfig()['price_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );

  private readonly store = inject(Store);
  private readonly productSelector = inject(ProductStoreSelector);
  private readonly modalsService = inject(ModalsService<ProductAddModalConfig>);
  private readonly contentDispatcher = inject(ContentStoreDispatcher);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);
  private readonly kioskGlobalSettings = inject(KioskGlobalSettingsService);
  private readonly productService = inject(ProductService);
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly kioskModalsService =
    inject<ModalsService<[KioskProductOverviewModalConfig, ProductAddModalConfig]>>(ModalsService);

  protected readonly isKioskLayout = this.settingsStoreSelector.isKioskLayout;

  protected readonly tagColors$ =
    this.marketplaceStoreSelector.selectMarketPlaceProductLabelsTagColors;

  /** Match shop product list cards: white fill when content builder has no cardColor. */
  protected readonly productBackgroundColor = computed<IContentBuilderFieldColor>(
    () =>
      this.cardColor() ?? {
        value: '#FFFFFF',
        type: ColorFieldTypesValues.Hex,
      },
  );

  protected readonly productNameColor = computed<IContentBuilderFieldColor>(
    () =>
      this.textColor() ?? {
        value: 'neutral-900',
        type: ColorFieldTypesValues.Palette,
      },
  );

  protected readonly productPriceColor = computed<IContentBuilderFieldColor>(
    () =>
      this.priceColor() ?? {
        value: 'neutral-900',
        type: ColorFieldTypesValues.Palette,
      },
  );

  protected readonly productCardColumns = computed(() => {
    return this.kioskGlobalSettings.productCardColSpan() === 2 ? 2 : 3;
  });

  private readonly settingsParameters = this.store.selectSignal(SELECT_SETTINGS_PARAMETERS);

  private readonly currentDistributionMode = computed<string | null>(() => {
    const parameters = this.settingsParameters();
    const forcedMode = parameters?.force_distribution_mode?.trim();
    if (forcedMode) {
      return forcedMode;
    }
    const selectedMode = parameters?.distribution_mode?.trim();
    if (selectedMode) {
      return selectedMode;
    }
    return null;
  });

  private readonly eligiblePromotions = this.store.selectSignal(selectEligiblePromotionsData);

  private readonly storeProducts = computed(() => this.productSelector.selectProducts() ?? []);

  /** Products to display in the carousel */
  protected readonly products = computed<DoodProductModel[]>(() => {
    const { orderedProductIds, productIdToPromotionNames } = this.buildPromotionMapsFromEligible(
      this.eligiblePromotions() ?? [],
      this.currentDistributionMode(),
    );
    if (orderedProductIds.length === 0) return [];
    return this.resolveFromProductList(
      this.storeProducts(),
      orderedProductIds,
      productIdToPromotionNames,
    );
  });

  protected readonly hasProducts = computed(() => this.products().length > 0);

  protected addProductDispatcher(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: {
          channel: 'promotion',
          reference: 'promotion_block',
        },
      }),
    );
  }

  protected openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.kioskModalsService.open('kiosk-add-product-overview-modal', undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        background: this.buttonTextColor(),
        accent: this.secondaryColor(),
      },
    });
  }

  protected isProductAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }

  /**
   * Build ordered product ids and label map from eligible promotions (display_in_promotion_block / display_label_in_product_block).
   */
  protected buildPromotionMapsFromEligible(
    promotions: EligiblePromotion[],
    currentDistributionMode: string | null,
  ): {
    orderedProductIds: string[];
    productIdToPromotionNames: Map<string, string[]>;
  } {
    const seenIds = new Set<string>();
    const orderedProductIds: string[] = [];
    const productIdToPromotionNames = new Map<string, string[]>();

    for (const p of promotions || []) {
      if (p.display_in_promotion_block !== true) continue;

      const promotionProducts = p.products ?? [];
      for (const ep of promotionProducts) {
        const pid = getEligiblePromotionProductId(ep);
        if (!pid) continue;
        if (!seenIds.has(pid)) {
          seenIds.add(pid);
          orderedProductIds.push(pid);
        }
        if (
          p.display_label_in_product_block === true &&
          p.title &&
          modeMatchesPromotionAllowedModes(currentDistributionMode, p.allowed_distribution_modes)
        ) {
          const names = productIdToPromotionNames.get(pid) ?? [];
          if (!names.includes(p.title)) {
            names.push(p.title);
            productIdToPromotionNames.set(pid, names);
          }
        }
      }
    }
    return { orderedProductIds, productIdToPromotionNames };
  }

  private isProductDisplayable(product: DoodProductModel): boolean {
    return (
      !!product[ProductKeys.Id] &&
      !!product[ProductKeys.Name] &&
      !product[ProductKeys.Hidden] &&
      this.productService.isAvailable(product)
    );
  }

  /** Resolve from flat product list (e.g. store); preserve promotion order. */
  protected resolveFromProductList(
    products: DoodProductModel[],
    orderedProductIds: string[],
    productIdToPromotionNames: Map<string, string[]>,
  ): DoodProductModel[] {
    const byId = new Map<string, DoodProductModel>();
    for (const product of products ?? []) {
      const id = product[ProductKeys.Id];
      if (!id || byId.has(id)) continue;
      if (!orderedProductIds.includes(id)) continue;
      if (!this.isProductDisplayable(product)) continue;
      const promotionNames = productIdToPromotionNames.get(id);
      const labels = this.mergePromotionLabels(product[ProductKeys.Labels], promotionNames);
      byId.set(id, { ...product, [ProductKeys.Labels]: labels });
    }
    return orderedProductIds.filter(id => byId.has(id)).map(id => byId.get(id)!);
  }

  /** Append synthetic labels for promotion titles so they display like product labels. */
  protected mergePromotionLabels(
    existingLabels: IProductLabel[] | undefined,
    promotionNames: string[] | undefined,
  ): IProductLabel[] {
    const existing = existingLabels ?? [];
    if (!promotionNames?.length) return existing;
    const synthetic: IProductLabel[] = promotionNames.map((name, index) => ({
      id: `promo-${name}-${index}`,
      handle: name.toLowerCase().replace(/\s+/g, '_'),
      line_through: false,
      name,
      color: '',
      visible: true,
      filtereable: false,
      position: index,
      entity_owner: { entity_id: '', entity_type: 'marketplace' },
    }));
    return [...existing, ...synthetic];
  }

  onProductClick(product: DoodProductModel): void {
    this.contentDispatcher.updateProduct({
      shopIsNotActive: false,
      shopId: product[ProductKeys.ShopId],
      productId: product[ProductKeys.Id],
    });
    this.modalsService.open('product-add-modal', undefined, {
      allowAddToCart: true,
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PromotionBlockComponent, { 'keyAliases': {} });
